// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MITs
import { Component, ChangeDetectionStrategy, computed, input } from '@angular/core';
import { NgxEchartsModule } from 'ngx-echarts';
import { userTechnicalRow } from '@lfx/core/gql';
import { chartConfig } from './line-echarts.features';
import { ShortNumberPipe } from '@lfx/shared/pipes/short-number.pipe';

@Component({
  selector: 'lfx-line-echarts',
  templateUrl: './line-echarts.component.html',
  styleUrls: ['./line-echarts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgxEchartsModule],
  host: {
    '[class.sparkline-mode]': 'sparkline()',
    '[style.width]': '"100%"',
    '[style.height]':  '"100%"',
    '[style.display]':  '"block"'
  }
})
export class LineEchartsComponent {
  // Modern input signals
  readonly yAxisName = input('# OF CONTRIBUTIONS');
  readonly chartData = input<userTechnicalRow[]>([]);
  readonly fnTableRowsMap = input(() => ({}));
  readonly useQuarter = input<boolean>(false);
  readonly sparkline = input<boolean>(false); // New sparkline mode input
  readonly sparklineData = input<number[]>([]);
  readonly lineType = input<'solid' | 'dashed' | 'dotted'>('solid'); // Line style type
  readonly lineColor = input('#009AFF'); // Customizable line color
  readonly hideLegend = input<boolean>(false); // Hide legend

  // Series data (automatically detects single vs multi-series)
  readonly seriesData = input<{
    data: number[];
    lineType?: 'solid' | 'dashed' | 'dotted';
    color?: string;
    fillOpacity?: number;
    label?: string;
    dashArray?: number[]; // Custom dash pattern [dash, gap]
  }[]>([]); // Data series configuration
  readonly xAxisLabels = input<string[]>([]);

  // Modern computed signal for chart options
  protected chartOption = computed(() => {
    if (this.sparkline()) {
      // Use sparkline configuration
      if (this.seriesData().length > 1) {
        // Multi-series sparkline
        return this.getMultiSeriesSparklineConfig();
      } else {
        // Single series sparkline
        return this.getSparklineConfig();
      }
    } else {
      // Use regular chart configuration
      const hasComplexData = this.chartData().length > 0;
      const hasSeriesData = this.seriesData().length > 0;

      if (hasSeriesData) {
        // Use seriesData for full charts (single or multi-series automatically detected)
        return this.getSeriesLineConfig();
      } else if (hasComplexData) {
        // Use complex userTechnicalRow structure (backward compatibility)
        return chartConfig({
          chartData: this.chartData(),
          fnTableRowsMap: this.fnTableRowsMap(),
          yAxisName: this.yAxisName(),
          useQuarter: this.useQuarter(),
        });
      }
      return {};
    }
  });

  private shortNumberPipe = new ShortNumberPipe();

  /**
   * Get sparkline data - either from explicit sparklineData input or derive from seriesData
   */
  private getSparklineData(): number[] {
    // If sparklineData is explicitly provided, use it
    if (this.sparklineData().length > 0) {
      return this.sparklineData();
    }

    // Otherwise, derive from first series in seriesData
    const seriesData = this.seriesData();
    if (seriesData.length > 0 && seriesData[0].data) {
      return seriesData[0].data;
    }

    // Fallback to empty array
    return [];
  }

  private getSparklineConfig(): object {
    const data = this.getSparklineData();
    const isSinglePoint = data.length === 1;

    return {
      animation: false,
      grid: {
        left: 0,
        right: 0,
        top: 8,  // Add small top padding to prevent line touching edge
        bottom: 8, // Add small bottom padding for balance
        containLabel: false
      },
      xAxis: {
        type: 'category',
        show: false,
        data: data.map((_, index) => index),
        boundaryGap: false, // Line charts don't need boundary gap
        axisTick: {
          show: false
        },
        axisLine: {
          show: false
        }
      },
      yAxis: {
        type: 'value',
        show: false
      },
      series: [{
        type: 'line',
        data: data,
        smooth: true,
        symbol: isSinglePoint ? 'circle' : 'none',
        symbolSize: 8,
        lineStyle: {
          color: this.lineColor(),
          width: 2,
          type: this.lineType()
        },
        itemStyle: {
          color: this.lineColor()
        },
        areaStyle: {
          color: this.createAreaGradient(this.lineColor(), 0.1)
        }
      }],
      tooltip: {
        show: false
      }
        };
  }

  /**
   * Get series line chart configuration for full charts using seriesData
   */
  private getSeriesLineConfig(): object {
    // Get max data length for x-axis
    const seriesData = this.seriesData();
    const maxDataLength = Math.max(...seriesData.map(s => s.data.length));
    const xAxisLabels = this.xAxisLabels();
    const xAxisData = xAxisLabels.length > 0
      ? xAxisLabels
      : Array.from({ length: maxDataLength }, (_, index) => `Category ${index + 1}`);

    // Default line chart configuration (from LFX Insights demo)
    const defaultLineOption = {
      grid: {
        top: '5%',
        left: '12%',
        right: '8%',
        bottom: '15%'
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        axisLabel: {
          fontSize: 12,
          color: '#64748B',
          fontFamily: 'Inter'
        },
        axisLine: {
          show: false
        },
        splitLine: {
          show: false
        },
        axisTick: {
          show: false
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          fontSize: 12,
          color: '#64748B',
          fontFamily: 'Inter',
          formatter: (value: number) => this.shortNumberPipe.transform(value)
        },
        splitLine: {
          lineStyle: {
            color: '#F3F4F6',
            type: 'dashed'
          }
        },
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'none'
        },
        backgroundColor: '#FFFFFF',
        borderColor: '#E5E7EB',
        borderWidth: 1,
        textStyle: {
          color: '#374151',
          fontFamily: 'Inter'
        }
      }
    };

    return {
      animation: true,
      ...defaultLineOption,
      xAxis: {
        ...defaultLineOption.xAxis,
        data: xAxisData
      },
      series: seriesData.map((series, index) => {
        const isSinglePoint = series.data.length === 1;
        const seriesColor = series.color || this.getLFXColor(index);
        
        return {
          type: 'line',
          name: series.label || `Series ${index + 1}`,
          data: series.data,
          smooth: true,
          showSymbol: isSinglePoint, // Auto-enable for single points
          symbol: 'circle',
          symbolSize: 8,
          lineStyle: {
            color: seriesColor,
            width: series.lineType === 'dotted' ? 2 : 1, // Use exact demo widths
            type: series.dashArray ? [series.dashArray[0], series.dashArray[1]] : series.lineType || 'solid'
          },
          itemStyle: {
            color: seriesColor
          },
          areaStyle: {
            color: this.createAreaGradient(seriesColor, series.fillOpacity || 0.1)
          }
        };
      }),
      legend: seriesData.length > 1 && !this.hideLegend() ? {
        top: 10,
        textStyle: {
          color: '#374151',
          fontFamily: 'Inter',
          fontSize: 12
        }
      } : undefined
    };
  }

  /**
   * Get multi-series sparkline configuration for dual line charts
   */
  private getMultiSeriesSparklineConfig(): object {
    const seriesData = this.seriesData();
    const maxDataLength = Math.max(...seriesData.map(s => s.data.length));

    return {
      animation: false,
      grid: {
        left: 0,
        right: 0,
        top: 8,  // Add small top padding to prevent line touching edge
        bottom: 8, // Add small bottom padding for balance
        containLabel: false
      },
      xAxis: {
        type: 'category',
        show: false,
        data: Array.from({ length: maxDataLength }, (_, index) => index),
        boundaryGap: false
      },
      yAxis: {
        type: 'value',
        show: false
      },
      series: seriesData.map((series, index) => {
        const isSinglePoint = series.data.length === 1;
        const seriesColor = series.color || this.getLFXColor(index);
        
        return {
          type: 'line',
          data: series.data,
          smooth: true,
          showSymbol: isSinglePoint,
          symbol: 'circle',
          symbolSize: 8,
          lineStyle: {
            color: seriesColor,
            width: 1,
            type: series.dashArray ? [series.dashArray[0], series.dashArray[1]] : series.lineType || 'solid'
          },
          itemStyle: {
            color: seriesColor
          },
          areaStyle: {
            color: this.createAreaGradient(seriesColor, series.fillOpacity || 0.1)
          }
        };
      }),
      tooltip: {
        show: false
      }
    };
  }

  /**
   * Get LFX brand colors for series by index (from demo)
   * Limited to two supported colors for consistency
   */
  private getLFXColor(index: number): string {
    // LFX Insights Colors - only two colors supported
    const lfxColors = [
      '#0094FF', // brand.500 (primary blue)
      '#64748B', // neutral.500 (gray)
    ];
    return lfxColors[index % lfxColors.length];
  }

  /**
   * Get default color for series by index (legacy method)
   */
  private getDefaultColor(index: number): string {
    return this.getLFXColor(index);
  }

  /**
   * Convert hex color to rgba format (from codebase)
   */
  private hexToRgba(hex: string, alpha = 1): string {
    // Remove the hash if present
    let cleanHex = hex.replace('#', '');

    // Handle 3-digit hex codes by duplicating each digit
    if (hex.length === 4 || (hex.length === 3 && !hex.startsWith('#'))) {
      const shortHex = hex.replace('#', '');
      cleanHex = `${shortHex[0]}${shortHex[0]}${shortHex[1]}${shortHex[1]}${shortHex[2]}${shortHex[2]}`;
    }

    // Parse the hex values
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);

    // Return rgba string
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  /**
   * Convert to gradient color (from codebase)
   */
  private convertToGradientColor(color: string, offsetStart = 0.1, offsetEnd = 1): object {
    return {
      type: 'linear',
      x: 0,
      y: 0,
      x2: 0,
      y2: 1,
      colorStops: [
        {
          offset: offsetStart,  // 0.1 (10% from top)
          color,               // Start color with opacity already applied
        },
        {
          offset: offsetEnd,   // 1.0 (100% at bottom)
          color: 'rgba(255, 255, 255, 0)', // Transparent white
        }
      ]
    };
  }

  /**
   * Create area gradient using codebase logic (from demo)
   */
  private createAreaGradient(color: string, opacity = 0.1): object {
    return this.convertToGradientColor(this.hexToRgba(color, opacity));
  }

}
